interface WorkbookMutationQueueState {
  pendingCount: number;
  tail: Promise<void>;
  controller: AbortController;
}

interface WorkbookMutationQueueContext {
  hasFollowingMutations: () => boolean;
  signal: AbortSignal;
}

const queues = new Map<string, WorkbookMutationQueueState>();

const queueKey = (businessId: string, runId: string) =>
  `${businessId}:${runId}`;

const createQueueState = (): WorkbookMutationQueueState => ({
  pendingCount: 0,
  tail: Promise.resolve(),
  controller: new AbortController(),
});

const workbookQueueAbortError = () =>
  new DOMException("Workbook mutation queue stopped", "AbortError");

export const enqueueWorkbookMutation = <T>({
  businessId,
  runId,
  execute,
}: {
  businessId: string;
  runId: string;
  execute: (context: WorkbookMutationQueueContext) => Promise<T>;
}): Promise<T> => {
  const key = queueKey(businessId, runId);
  const state = queues.get(key) ?? createQueueState();
  state.pendingCount += 1;
  queues.set(key, state);

  const execution = state.tail.then(() => {
    if (state.controller.signal.aborted) throw workbookQueueAbortError();

    return execute({
      hasFollowingMutations: () => state.pendingCount > 1,
      signal: state.controller.signal,
    });
  });
  state.tail = execution.then(
    () => undefined,
    () => undefined,
  );

  const settle = () => {
    state.pendingCount -= 1;
    if (state.pendingCount === 0) {
      if (queues.get(key) === state) queues.delete(key);
    }
  };
  void execution.then(settle, settle);

  return execution;
};

export const cancelWorkbookMutationQueue = (
  businessId: string,
  runId: string,
) => {
  const key = queueKey(businessId, runId);
  const state = queues.get(key);
  if (!state) return;

  queues.delete(key);
  state.controller.abort();
};
